import { verificationNeed, type VerificationNeed } from '@/lib/verificationErrors';

/**
 * A tiny store behind the "Confirm it's you" dialog, kept out of the component
 * file so contexts and hooks can ask for verification without importing UI.
 *
 * Any catch block hands its error to `requestVerificationFor`. When the error
 * is a two-step refusal from the server and a dialog host is on screen, the
 * dialog opens and the caller skips its own error toast.
 */

export type VerificationPrompt = { need: VerificationNeed; action: string } | null;

let current: VerificationPrompt = null;
const listeners = new Set<() => void>();

function emit(next: VerificationPrompt) {
  current = next;
  listeners.forEach(listener => listener());
}

export function subscribeVerificationPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function currentVerificationPrompt(): VerificationPrompt {
  return current;
}

/**
 * `action` finishes the sentence "You can … now" (for example "export the
 * workspace"). Returns true when the dialog was opened.
 */
export function requestVerificationFor(error: unknown, action: string): boolean {
  const need = verificationNeed(error);
  // No host on screen: the caller shows its ordinary error instead.
  if (!need || listeners.size === 0) return false;
  emit({ need, action });
  return true;
}

export function closeVerificationPrompt(): void {
  emit(null);
}
